import type { Component } from 'vue';
import { reactive, watch } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useModalComponent } from '../modals/useModal';

export type NotificationLevel = 'default' | 'danger' | 'warning' | 'success';
export type NotificationType = 'nudge' | 'announcement';

export type NotificationOptions = {
  id?: string;
  level?: NotificationLevel;
  timeout?: number;
  container?: string;
  to?: RouteLocationRaw;
  actions?: ({
    text: string;
    as: 'primary' | 'secondary';
  } & (
    | {
        handler: () => void;
      }
    | {
        to: RouteLocationRaw;
      }
  ))[];
  group?: string;
};

export type Notification = {
  props: Record<string, any>;
  component: Component;
  options?: NotificationOptions;
};

export const queue = reactive<{
  nudge: Notification[];
  shove: Notification[];
  announcement: Notification[];
}>({
  nudge: [],
  shove: [],
  announcement: [],
});

watch(
  () => queue.nudge.length,
  (length, previousLength) => {
    // If there is a notification to show in the queue AND the queue
    // was empty previously, OR the queue has gotten shorter.
    if (length > 0 && (previousLength === 0 || length < previousLength)) {
      const { props, options, component } = queue.nudge[0];
      const timeout = options?.timeout || 2500;

      useModalComponent(component, { container: 'nudge', timeout, ...options }).open(props);

      // Remove the notification from the queue after it has been shown.
      // Add a little extra time to the timeout to account for the animation.
      setTimeout(() => {
        queue.nudge.shift();
      }, timeout + 150);
    }
  },
);
